import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import { getDotPlayerConfig } from '../config/AppConfig';
import MetaTags from './SEO/MetaTags';
import SocialSharing from './SocialSharing';
import { generateSeriesSEO, generateStructuredData } from '../utils/SEOUtils';
import { resolvePdpCta } from '../utils/pdpAccess';

// Import user data service for watch progress
import userDataService from '../services/UserDataService';
import { useAuth } from '../hooks/useAuth';
import { useContinueWatching } from '../hooks/useContinueWatching';

// Import new reusable series components
import { SeasonEpisodeSelector, EpisodeCarousel, useSeriesNavigation } from './series';

// Common components that will be shared with MoviePDP
import ContentMetadata from './common/ContentMetadata';
import RelatedContent from './common/RelatedContent';
import CastCrew from './common/CastCrew';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorBoundary from './common/ErrorBoundary';

const SeriesPDP = ({ seriesId, onRelatedContentSelect }) => {
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const { isAuthenticated } = useAuth();

  // Get resumption data using the proper hook
  const { continueWatchingData, loading: resumptionLoading } = useContinueWatching();

  // Debug logging to see what's happening

  // Use the enhanced series navigation hook
  const {
    series,
    childChannels,
    currentChildChannel,
    episodes,
    selectedEpisode,
    relatedContent,
    loading,
    error,
    retry,
    handleSeasonSelect,
    handleEpisodeSelect,
    isPlaying,
    play,
    pause,
  } = useSeriesNavigation(seriesId, {
    autoPlay: false,
    onEpisodeChange: episode => {
      // Episode changed
    },
    onSeasonChange: season => {
      // Season changed
    },
  });

  // Enhanced series completion detection
  const checkSeriesCompletion = useCallback((resumptionData, seriesData) => {
    if (!resumptionData || !seriesData || !seriesData.child_channels) {
      return false;
    }

    // Get all episodes across all seasons
    const allEpisodes = [];
    seriesData.child_channels.forEach(season => {
      if (season.playlist && Array.isArray(season.playlist)) {
        allEpisodes.push(...season.playlist);
      }
    });

    if (allEpisodes.length === 0) {return false;}

    // Find the final episode (last episode of last season)
    const finalEpisode = allEpisodes[allEpisodes.length - 1];
    if (!finalEpisode) {return false;}

    // Check if final episode is in watch-again data (completed)
    const watchAgainData = userDataService.getWatchAgain() || [];
    const finalEpisodeInWatchAgain = watchAgainData.find(
      item => item.channel?._id === finalEpisode._id || item.video_id === finalEpisode.video_id,
    );

    // Check if final episode is NOT in continue-watching (fully completed)
    const continueWatchingData = userDataService.getContinueWatching() || [];
    const finalEpisodeInContinueWatching = continueWatchingData.find(
      item => item.channel?._id === finalEpisode._id || item.video_id === finalEpisode.video_id,
    );

    // Series is complete if final episode is in watch-again AND not in continue-watching
    return finalEpisodeInWatchAgain && !finalEpisodeInContinueWatching;
  }, []);

  // Enhanced watch progress calculation with proper series logic
  const watchProgress = useMemo(() => {
    if (!isAuthenticated || !series?._id || resumptionLoading) {
      return null;
    }

    // Get all resumption data
    const continueWatching = userDataService.getContinueWatching() || [];
    const watchAgain = userDataService.getWatchAgain() || [];
    const allResumptions = [...continueWatching, ...watchAgain];

    if (allResumptions.length === 0) {
      return null;
    }

    // Find resumption data for this series
    const seriesResumptions = allResumptions.filter(item => {
      // Check if this episode belongs to this series
      return (
        item.channel?.parent_channel?._id === series._id || item.parent_channel?._id === series._id
      );
    });

    if (seriesResumptions.length === 0) {
      return null;
    }

    // Get the most recent episode watched
    const latestResumption = seriesResumptions.reduce((latest, current) => {
      const latestTime = new Date(latest.updated_at || latest.created_at || 0);
      const currentTime = new Date(current.updated_at || current.created_at || 0);
      return currentTime > latestTime ? current : latest;
    });

    // Check if series is complete
    const isSeriesComplete = checkSeriesCompletion(allResumptions, series);

    const result = {
      progress: latestResumption.progress || 0,
      point: latestResumption.point || 0,
      duration: latestResumption.duration || 0,
      isSeries: true,
      isSeriesComplete,
      isInWatchAgain: latestResumption.progress >= 0.95,
      episodeData: {
        episodeId: latestResumption.channel?._id,
        episodeTitle: latestResumption.channel?.title || 'Episode',
        seasonNumber: latestResumption.channel?.season || 1,
        episodeNumber: latestResumption.channel?.episode || 1,
      },
      latestResumption,
    };

    return result;
  }, [
    isAuthenticated,
    series?._id,
    resumptionLoading,
    continueWatchingData,
    checkSeriesCompletion,
  ]);

  // Enhanced watch button logic with proper series completion detection and paywall
  const getSeriesWatchButtonInfo = useCallback(() => {
    if (!series) {
      return {
        text: 'Watch Now',
        className: 'pdp-play-button pdp-play-button-chillFree',
        icon: 'play',
        action: 'start',
      };
    }

    // Check if series is locked (paywall). ICOD purchase bundles can attach to
    // the series itself OR to the currently selected season (parent wrappers
    // stay unlocked while their child channels carry the lock).
    if (resolvePdpCta(series, currentChildChannel).action === 'buy') {
      return {
        text: 'Buy Now',
        className: 'pdp-play-button pdp-play-button-chillFree',
        icon: 'lock',
        action: 'buy',
      };
    }

    // No progress data - start from beginning
    if (!watchProgress) {
      return {
        text: 'Watch Now',
        className: 'pdp-play-button pdp-play-button-chillFree',
        icon: 'play',
        action: 'start',
      };
    }

    // Series is completely finished
    if (watchProgress.isSeriesComplete) {
      return {
        text: 'Watch Again',
        className: 'pdp-play-button pdp-play-button-chillFree',
        icon: 'replay',
        action: 'restart',
      };
    }

    // Current episode is partially watched
    if (watchProgress.progress < 0.95 && watchProgress.progress > 0) {
      return {
        text: 'Resume',
        className: 'pdp-play-button pdp-play-button-chillFree',
        icon: 'resume',
        action: 'resume',
      };
    }

    // Current episode is complete but series isn't finished
    if (watchProgress.progress >= 0.95) {
      return {
        text: 'Continue Watching',
        className: 'pdp-play-button pdp-play-button-chillFree',
        icon: 'play',
        action: 'continue',
      };
    }

    // Default fallback
    return {
      text: 'Watch Now',
      className: 'pdp-play-button pdp-play-button-chillFree',
      icon: 'play',
      action: 'start',
    };
  }, [series, watchProgress, currentChildChannel]);

  // Calculate episode progress for visual indicators
  const getEpisodeProgress = useCallback(
    episodeId => {
      if (!isAuthenticated || !continueWatchingData) {
        return null;
      }

      // Get all resumption data
      const continueWatching = userDataService.getContinueWatching() || [];
      const watchAgain = userDataService.getWatchAgain() || [];
      const allResumptions = [...continueWatching, ...watchAgain];

      // Find progress for this specific episode
      const episodeProgress = allResumptions.find(
        item => item.channel?._id === episodeId || item.video_id === episodeId,
      );

      if (!episodeProgress) {
        return null;
      }

      return {
        progress: episodeProgress.progress || 0,
        point: episodeProgress.point || 0,
        duration: episodeProgress.duration || 0,
        isCompleted: episodeProgress.progress >= 0.95,
        isInProgress: episodeProgress.progress > 0 && episodeProgress.progress < 0.95,
      };
    },
    [isAuthenticated, continueWatchingData],
  );

  // Determine next episode to watch based on progress
  const getNextEpisodeToWatch = useCallback(() => {
    if (!episodes || episodes.length === 0 || !isAuthenticated) {
      return episodes?.[0] || null;
    }

    // Find the first unwatched episode
    for (const episode of episodes) {
      const progress = getEpisodeProgress(episode._id);
      if (!progress || progress.progress < 0.95) {
        return episode;
      }
    }

    // If all episodes are watched, return the last one
    return episodes[episodes.length - 1];
  }, [episodes, getEpisodeProgress, isAuthenticated]);

  const nextEpisodeToWatch = getNextEpisodeToWatch();

  const watchButtonInfo = getSeriesWatchButtonInfo();

  // Format progress time
  const formatProgressTime = useCallback(seconds => {
    if (!seconds || seconds < 0) {return '0:00';}
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Format series episode info
  const formatSeriesEpisodeInfo = useCallback(episodeData => {
    if (!episodeData) {return '';}
    return `S${episodeData.seasonNumber} E${episodeData.episodeNumber} • ${episodeData.episodeTitle}`;
  }, []);

  // Enhanced watch navigation with smart episode selection and paywall
  const handleWatchNow = useCallback(
    (episode = null) => {
      if (!series?.slug) {
        console.warn('No series slug available for watch navigation');
        return;
      }

      // Check if action is 'buy' (series or current season is locked)
      if (watchButtonInfo.action === 'buy') {
        // Buy whichever channel actually carries the lock: the series itself,
        // or the current season when the parent wrapper is unlocked
        const { channelToBuy } = resolvePdpCta(series, currentChildChannel);
        const target = channelToBuy || series;
        navigate(`/checkout/${target._id}`, {
          state: { channel: target, parentChannel: series },
        });
        return;
      }

      let targetEpisode = episode;
      let watchUrl = `/watch/${series.slug}`;

      // If no specific episode provided, determine based on button action
      if (!targetEpisode) {
        switch (watchButtonInfo.action) {
          case 'resume':
            // Resume current episode from progress point
            if (watchProgress?.episodeData?.episodeId) {
              targetEpisode = episodes?.find(ep => ep._id === watchProgress.episodeData.episodeId);
            }
            break;

          case 'continue':
            // Continue to next episode
            targetEpisode = nextEpisodeToWatch;
            break;

          case 'restart':
            // Restart series from first episode
            targetEpisode = episodes?.[0];
            break;

          case 'start':
          default:
            // Start from first episode or next unwatched
            targetEpisode = nextEpisodeToWatch || episodes?.[0];
            break;
        }
      }

      // Add episode parameter if we have a target episode
      if (targetEpisode?._id) {
        watchUrl += `/${targetEpisode._id}`;
      }

      // Navigate to series watch page
      navigate(watchUrl);
    },
    [series, navigate, watchButtonInfo, watchProgress, nextEpisodeToWatch, episodes, currentChildChannel],
  );

  // Enhanced episode selection with progress awareness and paywall logic
  const handleEpisodeSelectOverride = useCallback(
    episode => {
      // Check if episode is locked
      const isEpisodeLocked = () => {
        // If episode has bypass_channel_lock flag, it's always unlocked
        if (episode.bypass_channel_lock === true) {
          return false;
        }

        // Check parent series lock status
        if (series?.subscription_access && series.subscription_access.unlocked === false) {
          return true; // Parent is locked
        }

        // Check season lock status
        if (currentChildChannel?.subscription_access && currentChildChannel.subscription_access.unlocked === false) {
          return true; // Season is locked
        }

        return false; // Episode is unlocked
      };

      if (isEpisodeLocked()) {
        // Navigate to checkout page
        // Determine which product to buy: full series or specific season
        const channelToBuy = series?.subscription_access?.unlocked === false 
          ? series // Buy full series
          : currentChildChannel; // Buy specific season

        navigate(`/checkout/${channelToBuy._id}`, {
          state: { channel: channelToBuy },
        });
      } else {
        // Episode is unlocked, proceed to watch
        handleWatchNow(episode);
      }
    },
    [handleWatchNow, getEpisodeProgress, series, currentChildChannel, navigate],
  );

  // Memoized event handlers
  const handlePlayClick = useCallback(() => {
    play();
  }, [play]);

  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAddToWatchlist = useCallback(() => {
    // This would be implemented with actual watchlist functionality
    // console.log('Added to watchlist:', series?.title);
    // Show a toast or notification
    alert(`Added "${series?.title}" to your watchlist`);
  }, [series?.title]);

  // Hero image with optimization
  const getHeroImage = useCallback(() => {
    if (!series) {return null;}

    const heroImageUrl =
      series.spotlight_poster ||
      series.poster ||
      series.theme?.image?.spotlight_poster ||
      series.theme?.image?.poster;

    if (!heroImageUrl) {return null;}

    return optimizeImageUrl(heroImageUrl, getResponsiveImagePreset('CHANNEL_HERO'));
  }, [series]);

  // Navigation buttons component for reuse across states
  const NavigationButtons = () => (
    <div className="flex items-center mb-4 space-x-4">
      <button onClick={handleBackClick} className="flex items-center text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clipRule="evenodd"
          />
        </svg>
        Back
      </button>
      <button onClick={() => navigate('/')} className="flex items-center text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
        Home
      </button>
    </div>
  );

  // Player mounting effect
  React.useEffect(() => {
    if (!isPlaying || !selectedEpisode?._id) {return;}

    const mountPlayer = async () => {
      if (window.DotPlayer.el) {
        window.DotPlayer.unmount();
      }
      if (window.DotPlayer && playerRef.current) {
        try {
          // Get DotPlayer configuration from centralized config
          const playerConfig = await getDotPlayerConfig(selectedEpisode._id, '#pdp-player', {
            autostart: true,
            controls: true,
          });

          const player = await window.DotPlayer.mount(playerConfig);
          await player.isPlayerLoaded();
          // console.log('🎬 Series player loaded successfully');
        } catch (error) {
          // console.error('❌ Failed to mount series player:', error);
        }
      }
    };

    mountPlayer();

    return () => {
      if (window.DotPlayer) {
        window.DotPlayer.unmount();
      }
    };
  }, [isPlaying, selectedEpisode?._id]);
  if (loading) {
    return (
      <div className="min-h-screen series-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          {/* Skeleton loading state */}
          <div className="animate-pulse">
            <div className="w-full mb-8 bg-gray-700 rounded-lg h-96"></div>
            <div className="w-3/4 h-10 mb-4 bg-gray-700 rounded"></div>
            <div className="w-1/4 h-4 mb-6 bg-gray-700 rounded"></div>
            <div className="w-full h-4 mb-2 bg-gray-700 rounded"></div>
            <div className="w-full h-4 mb-2 bg-gray-700 rounded"></div>
            <div className="w-5/6 h-4 mb-8 bg-gray-700 rounded"></div>

            <div className="flex mb-8 space-x-4">
              <div className="w-32 h-10 bg-gray-700 rounded"></div>
              <div className="w-32 h-10 bg-gray-700 rounded"></div>
            </div>

            <div className="w-48 h-6 mb-4 bg-gray-700 rounded"></div>
            <div className="flex space-x-4 overflow-x-auto">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex-shrink-0 w-64">
                  <div className="mb-2 bg-gray-700 rounded-lg h-36"></div>
                  <div className="w-3/4 h-4 mb-1 bg-gray-700 rounded"></div>
                  <div className="w-1/2 h-3 bg-gray-700 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen series-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          <div className="p-6 text-center border border-[#FCC834] rounded-lg bg-[#FCC834]/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-12 h-12 mx-auto mb-4 text-[#FCC834]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="mb-2 text-xl font-bold text-[#FCC834]">Error Loading Series</h2>
            <p className="mb-4 text-[#FCC834]/70">{error}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleBackClick}
                className="px-6 py-2 font-medium text-white rounded-full bg-[#FCC834] hover:bg-[#E0A800]"
              >
                Go Back
              </button>
              <button
                onClick={retry}
                className="px-6 py-2 font-medium text-white rounded-full bg-secondary hover:bg-[#2a2a2a]"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen series-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          <div className="py-12 text-center">
            <h2 className="mb-2 text-xl font-bold text-white">Series Not Found</h2>
            <p className="mb-4 text-white/70">The requested series could not be found.</p>
            <button
              onClick={handleBackClick}
              className="px-6 py-2 font-medium text-white rounded-full bg-[#FCC834] hover:bg-[#E0A800]"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const heroImage = getHeroImage();
  const title = series.title || 'Untitled Series';
  const description = series.description || 'No description available';
  const genres = series.genres?.join(', ') || 'Unknown genre';
  const rating = series.rating || 'Not rated';
  const releaseYear = series.year || 'Unknown year';
  const actors = series.actors || [];
  const directors = series.directors || [];

  // Generate SEO data
  const seoData = generateSeriesSEO(series);
  const structuredData = generateStructuredData(series, 'series');

  return (
    <div className="min-h-screen series-pdp bg-primary">
      {/* SEO Meta Tags */}
      <MetaTags {...seoData} />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="container px-4 py-8 mx-auto">
        <NavigationButtons />

        {isPlaying && selectedEpisode ? (
          <div className="mb-8">
            <div
              id="pdp-player"
              ref={playerRef}
              className="w-full aspect-video bg-black rounded-lg overflow-hidden"
              style={{ minHeight: 400 }}
            />
          </div>
        ) : (
          <div className="relative mb-8 overflow-hidden rounded-lg">
            {heroImage ? (
              <div className="relative">
                <img src={heroImage} alt={title} className="w-full h-[50vh] object-cover" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 bg-gradient-to-t from-[#1a1a1a]/90 via-[#1a1a1a]/60 to-transparent">
                  <h1 className="mb-2 text-3xl font-bold text-white md:text-4xl">{title}</h1>
                  <div className="flex flex-wrap items-center mb-4 text-sm text-white/70">
                    <span className="mr-3">{releaseYear}</span>
                    <span className="mr-3">•</span>
                    <span className="mr-3">{genres}</span>
                    {rating && (
                      <>
                        <span className="mr-3">•</span>
                        <span className="bg-[#FCC834]/20 px-2 py-0.5 rounded text-[#FCC834]">
                          {rating}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {/* Main Watch Now Button */}
                    <button onClick={() => handleWatchNow()} className={watchButtonInfo.className}>
                      {watchButtonInfo.icon === 'play' && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                      {watchButtonInfo.icon === 'resume' && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                          <circle cx="12" cy="12" r="2" fill="#FCC834" />
                        </svg>
                      )}
                      {watchButtonInfo.icon === 'replay' && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="1 4 1 10 7 10" />
                          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                        </svg>
                      )}
                      {watchButtonInfo.icon === 'lock' && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {watchButtonInfo.text}
                    </button>

                    {selectedEpisode && (
                      <button
                        onClick={handlePlayClick}
                        className="flex items-center px-6 py-2 font-medium text-white rounded-full bg-secondary hover:bg-[#2a2a2a]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 mr-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Play Preview
                      </button>
                    )}
                  </div>

                  {/* Continue Watching Progress Bar - Modern Style */}
                  {watchProgress && (
                    <div className="pdp-progress-container mt-4">
                      <div className="pdp-progress-bar">
                        <div
                          className="pdp-progress-fill"
                          style={{ width: `${watchProgress.progress * 100}%` }}
                        />
                      </div>
                      {/* Modern progress text layout */}
                      <div className="pdp-progress-text-container">
                        {watchProgress.isSeries ? (
                          <>
                            <div className="pdp-progress-episode-info">
                              {formatSeriesEpisodeInfo(watchProgress.episodeData)}
                            </div>
                            <div className="pdp-progress-time">
                              {formatProgressTime(watchProgress.point)} of{' '}
                              {formatProgressTime(watchProgress.duration)}
                            </div>
                          </>
                        ) : (
                          <div className="pdp-progress-time">
                            {formatProgressTime(watchProgress.point)} of{' '}
                            {formatProgressTime(watchProgress.duration)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 h-[50vh] flex items-center justify-center">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-16 h-16 mx-auto mb-4 text-white/30"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                    />
                  </svg>
                  <h1 className="mb-2 text-3xl font-bold text-white md:text-4xl">{title}</h1>
                  <div className="flex flex-wrap items-center justify-center mb-4 text-sm text-white/70">
                    <span className="mx-2">{releaseYear}</span>
                    <span>•</span>
                    <span className="mx-2">{genres}</span>
                    {rating && (
                      <>
                        <span>•</span>
                        <span className="mx-2 bg-[#FCC834]/20 px-2 py-0.5 rounded text-[#FCC834]">
                          {rating}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-white">About this Series</h2>
          <p className="text-white/70">{description}</p>

          {/* Social Sharing */}
          <div className="mt-4">
            <SocialSharing
              channelSlug={series?.slug}
              title={title}
              description={description}
              image={heroImage || series?.spotlight_poster || series?.poster}
            />
          </div>
        </div>

        {childChannels.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Episodes</h2>
              <SeasonEpisodeSelector
                seasons={childChannels}
                selectedSeason={currentChildChannel}
                onSeasonSelect={handleSeasonSelect}
                variant="modal"
                placeholder="Select Season"
                showEpisodeCount={true}
              />
            </div>

            <ErrorBoundary fallback="Failed to load episodes">
              <Suspense fallback={<LoadingSpinner message="Loading episodes..." />}>
                <EpisodeCarousel
                  episodes={episodes}
                  selectedEpisode={selectedEpisode}
                  onEpisodeSelect={handleEpisodeSelectOverride}
                  variant="modal"
                  showPlayButtons={true}
                  showDescriptions={true}
                  layout="carousel"
                  slidesToShow={4}
                  loading={loading}
                  emptyMessage="No episodes available for this season"
                  // Enhanced props for progress indicators
                  getEpisodeProgress={getEpisodeProgress}
                  nextEpisodeToWatch={nextEpisodeToWatch}
                  showProgressIndicators={true}
                  highlightNextEpisode={true}
                  // Paywall props
                  seriesSubscriptionAccess={series?.subscription_access}
                  seasonSubscriptionAccess={currentChildChannel?.subscription_access}
                />
              </Suspense>
            </ErrorBoundary>
          </div>
        )}

        <ErrorBoundary fallback="Failed to load cast and crew information">
          <Suspense fallback={<LoadingSpinner message="Loading cast and crew..." />}>
            <CastCrew actors={actors} directors={directors} className="mb-8" />
          </Suspense>
        </ErrorBoundary>

        <ErrorBoundary fallback="Failed to load related content">
          <Suspense fallback={<LoadingSpinner message="Loading related content..." />}>
            {relatedContent.length > 0 && (
              <RelatedContent items={relatedContent} onSelect={onRelatedContentSelect} />
            )}
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default SeriesPDP;
